import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { allCustomerQuery } from '@/features/Site/graphql/query';
import { Customer, Site } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { FeatureName } from '@/models/role.models';
import hasPermission from '@/utils/permission.function';
import { hasNonEmptyField } from '@/utils/search-param.function';
import { useQuery } from '@tanstack/react-query';
import { Table } from '@tanstack/react-table';
import { Columns2, Plus, X } from 'lucide-react';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { AddSiteDialog } from './AddSiteDialog';

export type SiteFilter = {
  name?: string | null;
  customer?: string | null;
  notes?: 'yes' | 'no' | null;
};

type ListSiteHeaderProps = {
  table: Table<Site>;
  filters?: SiteFilter;
  setFilters: Dispatch<SetStateAction<SiteFilter | undefined>>;
};

export default function ListSiteHeader({
  table,
  filters,
  setFilters,
}: ListSiteHeaderProps) {
  const [addSiteDialogOpen, setAddSiteDialogOpen] = useState<boolean>();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const showResetFiltersButton = hasNonEmptyField(filters);

  const { data: allCustomersData } = useQuery({
    queryKey: ['All Customers'],
    queryFn: () =>
      execute({
        query: allCustomerQuery,
      }),
    select: (data) => data,
    staleTime: 30000,
  });

  useEffect(() => {
    if (allCustomersData) {
      setCustomers(allCustomersData?.customer as Customer[]);
    }
  }, [allCustomersData]);

  const handleNameFilterValueChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setFilters({ ...filters, name: event.target.value });
  };

  const handleClickResetFilters = () => {
    setFilters({});
  };

  const handleCustomerSelectionChange = (value: string) => {
    setFilters({ ...filters, customer: value === 'all' ? undefined : value });
  };

  const handleNotesFilterChange = (value: string) => {
    setFilters({
      ...filters,
      notes: value === 'all' ? null : (value as 'yes' | 'no'),
    });
  };

  const handleToggleDialogOpen = () => {
    setAddSiteDialogOpen(!addSiteDialogOpen);
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <Input
          placeholder="Filter by name or id..."
          onChange={handleNameFilterValueChange}
          value={filters?.name ?? ''}
        />

        <div>
          <Select
            value={filters?.customer ?? ''}
            onValueChange={handleCustomerSelectionChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={'all'} disabled={!filters?.customer}>
                  All
                </SelectItem>
                {customers.map((customer, key) => {
                  return (
                    <SelectItem value={customer.id} key={key}>
                      {customer.name}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Select
            value={filters?.notes ?? ''}
            onValueChange={handleNotesFilterChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by notes" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={'all'} disabled={!filters?.notes}>
                  All
                </SelectItem>
                <SelectItem value={'yes'}>Yes</SelectItem>
                <SelectItem value={'no'}>No</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-start gap-2 md:col-span-2 md:justify-end">
          {showResetFiltersButton && (
            <Button variant={'outline'} onClick={handleClickResetFilters}>
              <X />
              Reset
            </Button>
          )}
          {hasPermission(FeatureName.CREATE_SITE) && (
            <Button
              variant={'outline'}
              onClick={() => {
                setAddSiteDialogOpen(true);
              }}
            >
              <Plus />
              Add Site
            </Button>
          )}
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="ml-auto">
                  <Columns2 />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  ?.getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {addSiteDialogOpen && (
        <AddSiteDialog
          customers={customers}
          onToggleOpen={handleToggleDialogOpen}
          open={addSiteDialogOpen}
        />
      )}
    </>
  );
}
